/**
 * Donation Component
 * Handles donation form functionality with predefined amounts and custom input
 */
class DonationComponent extends HTMLElement {
  constructor() {
    super();
    
    this.amountRadios = this.querySelectorAll('.amount-radio');
    this.customAmountInput = this.querySelector('.custom-amount-input');
    this.customAmountRadio = this.querySelector('input[name="donation_amount"][value="custom"]');
    this.submitButton = this.querySelector('[type="submit"]');
    this.currencySymbol = this.dataset.currencySymbol || '$';
    
    // Free gift settings
    this.freeGiftEnabled = this.dataset.freeGiftEnabled === 'true';
    this.freeGiftThreshold = parseFloat(this.dataset.freeGiftThreshold) || 100;
    this.freeGiftProductId = this.dataset.freeGiftProductId;
    this.freeGiftPrice = parseFloat(this.dataset.freeGiftPrice) || 0;
    this.freeGiftTitle = this.dataset.freeGiftTitle || '';
    this.enableFreeGiftModal = this.dataset.enableFreeGiftModal === 'true';
    
    // Modal and form references
    this.modal = this.querySelector('modal-dialog');
    this.form = this.querySelector('form');
    this.freeGiftPropertyInput = this.querySelector('[data-free-gift-property]');
    
    // Track original parent for modal (needed when modal is inside cart drawer)
    this.modalOriginalParent = null;
    
    // Track if user is in the middle of gift selection
    this.pendingDonationAmount = null;
    this.giftAccepted = null; // null = not decided, true = accepted, false = declined
    this.existingDonationToUpdate = null; // Reference to existing donation being updated
  }

  connectedCallback() {
    this.initializeEventListeners();
    this.initializeCustomAmountInput();
    this.initializeFreeGiftModal();
  }

  initializeEventListeners() {
    // Handle amount selection
    this.amountRadios.forEach(radio => {
      radio.addEventListener('change', (e) => this.handleAmountChange(e));
    });

    // Handle custom amount input
    if (this.customAmountInput) {
      this.customAmountInput.addEventListener('input', (e) => this.handleCustomAmountInput(e));
      this.customAmountInput.addEventListener('focus', (e) => this.handleCustomAmountFocus(e));
    }

    // Intercept form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    }
  }

  initializeCustomAmountInput() {
    // Set up custom amount input behavior
    if (this.customAmountInput) {
      this.customAmountInput.addEventListener('keydown', (e) => {
        // Allow only numbers, decimal point, and control keys
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        const isNumber = /[0-9]/.test(e.key);
        const isDecimal = e.key === '.' || e.key === ',';
        const isControl = e.ctrlKey || e.metaKey;
        
        if (!allowedKeys.includes(e.key) && !isNumber && !isDecimal && !isControl) {
          e.preventDefault();
        }
      });
    }
  }

  handleAmountChange(event) {
    const selectedValue = event.target.value;
    
    // Clear custom input if predefined amount is selected
    if (selectedValue !== 'custom' && this.customAmountInput) {
      this.customAmountInput.value = '';
    }
    
    // Update submit button state
    this.updateSubmitButton();
    this.adjustCustomPrice();
  }

  handleCustomAmountInput(event) {
    const value = event.target.value;
    
    // Select custom radio if user is typing
    if (value && this.customAmountRadio) {
      this.customAmountRadio.checked = true;
    }
    
    // Validate and format the input
    this.validateCustomAmount(value);
    // Update submit button state
    this.updateSubmitButton();
    this.adjustCustomPrice();
  }

  handleCustomAmountFocus(event) {
    // Select custom radio when focusing on input
    if (this.customAmountRadio) {
      this.customAmountRadio.checked = true;
    }
    // Update submit button state when custom is focused
    this.updateSubmitButton();
  }

  validateCustomAmount(value) {
    if (!value) return true;
    
    // Remove any non-numeric characters except decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      const formattedValue = parts[0] + '.' + parts.slice(1).join('');
      this.customAmountInput.value = formattedValue;
    } else {
      this.customAmountInput.value = cleanValue;
    }
    
    // Check if value is valid number
    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue) || numValue < 0) {
      this.customAmountInput.style.borderColor = '#e74c3c';
      return false;
    } else {
      this.customAmountInput.style.borderColor = '';
      return true;
    }
  }

  // Helper method to check if custom amount is valid for submission
  isCustomAmountValidForSubmission() {
    const selectedAmount = this.getSelectedAmount();
    
    if (selectedAmount === 'custom') {
      const customValue = this.customAmountInput.value.trim();
      
      // Check if custom amount is empty
      if (!customValue) {
        return false;
      }
      
      // Check if custom amount is a valid number greater than 0
      const numValue = parseFloat(customValue);
      if (isNaN(numValue) || numValue <= 0) {
        return false;
      }
    }
    
    return true;
  }

  // Updated method to handle submit button state with validation
  updateSubmitButton() {
    if (!this.submitButton) return;
    
    const selectedAmount = this.getSelectedAmount();
    
    // If no amount is selected, disable button
    if (!selectedAmount) {
      this.submitButton.disabled = true;
      return;
    }
    
    // If predefined amount is selected, enable button
    if (selectedAmount !== 'custom') {
      this.submitButton.disabled = false;
      return;
    }
    
    // If custom amount is selected, validate it
    if (this.isCustomAmountValidForSubmission()) {
      this.submitButton.disabled = false;
    } else {
      this.submitButton.disabled = true;
    }
  }

  // Keep the old method for backward compatibility
  enableSubmitButton() {
    this.updateSubmitButton();
  }

  getSelectedAmount() {
    const selectedRadio = this.querySelector('input[name="donation_amount"]:checked');
    return selectedRadio ? selectedRadio.value : null;
  }

  adjustCustomPrice() {
    const selectedAmount = this.getSelectedAmount();
    if (selectedAmount === 'custom') {
      const customValue = this.customAmountInput.value.trim();
      this.querySelector('input[name="properties[_custom_price]"]').value = customValue;
    } else {
      this.querySelector('input[name="properties[_custom_price]"]').value = selectedAmount;
    }
  }

  /**
   * Initialize free gift modal event listeners
   */
  initializeFreeGiftModal() {
    if (!this.freeGiftEnabled || !this.modal) return;

    const acceptButton = this.modal.querySelector('[data-accept-gift]');
    const declineButton = this.modal.querySelector('[data-decline-gift]');

    if (acceptButton) {
      acceptButton.addEventListener('click', (e) => this.handleAcceptGift(e));
    }

    if (declineButton) {
      declineButton.addEventListener('click', (e) => this.handleDeclineGift(e));
    }

    // Listen for variant changes to update adjusted amount and selected label
    const variantSelector = this.modal.querySelector('variant-selects');
    if (variantSelector) {
      // Use event delegation for radio buttons
      variantSelector.addEventListener('change', (event) => {
        const radio = event.target;
        
        // Only handle radio button changes
        if (radio.type !== 'radio' || !radio.hasAttribute('data-free-gift-option')) {
          return;
        }
        
        // Update the selected option label
        const fieldset = radio.closest('fieldset');
        if (fieldset) {
          const selectedLabel = fieldset.querySelector('.selected-option');
          if (selectedLabel) {
            selectedLabel.textContent = radio.value;
          }
        }
        
        // Update adjusted amount display
        if (this.pendingDonationAmount) {
          const adjustedAmount = this.calculateAdjustedAmount(this.pendingDonationAmount);
          const adjustedAmountElement = this.modal.querySelector('[data-adjusted-amount-value]');
          if (adjustedAmountElement) {
            adjustedAmountElement.textContent = this.formatCurrency(adjustedAmount);
          }
        }
        
        // Clear stored variant so it's recalculated on next get
        this.selectedFreeGiftVariantId = null;
      });
    }
  }

  /**
   * Handle form submission - check threshold and show modal if needed
   */
  async handleFormSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    // Get donation amount
    const donationAmount = this.getDonationAmount();

    // If free gift modal is disabled, add to cart directly
    if (!this.enableFreeGiftModal) {
      this.addToCart(donationAmount, null);
      return;
    }

    // If free gift not enabled or not configured, add to cart directly
    if (!this.freeGiftEnabled || !this.freeGiftProductId) {
      this.addToCart(donationAmount, null);
      return;
    }

    // If user already made a decision (accepted or declined), this shouldn't happen
    // but handle it just in case
    if (this.giftAccepted !== null) {
      this.addToCart(donationAmount, this.giftAccepted);
      return;
    }
    
    // Check if amount exceeds threshold
    if (donationAmount >= this.freeGiftThreshold) {
      // Before showing modal, check if there's already a decision in the cart
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        const donationVariantId = this.form.querySelector('input[name="id"]').value;
        const existingDonation = this.findExistingDonation(cart, donationVariantId);
        
        if (existingDonation && existingDonation.properties._free_gift_opt_in !== undefined) {
          // Decision already exists in cart - don't show modal, just add to cart
          this.addToCart(donationAmount, null);
          return;
        }
      } catch (error) {
        console.error('Error checking cart:', error);
        // Continue to show modal if check fails
      }
      
      // No existing decision - show modal
      this.pendingDonationAmount = donationAmount;
      this.showFreeGiftModal(donationAmount);
    } else {
      // Below threshold - add to cart without showing modal (no decision made)
      this.addToCart(donationAmount, null);
    }
  }

  /**
   * Get the current donation amount
   */
  getDonationAmount() {
    const selectedAmount = this.getSelectedAmount();
    
    if (selectedAmount === 'custom') {
      const customValue = this.customAmountInput.value.trim();
      return parseFloat(customValue) || 0;
    } else {
      return parseFloat(selectedAmount) || 0;
    }
  }

  /**
   * Show the free gift modal with calculated adjusted amount
   */
  showFreeGiftModal(donationAmount) {
    // Don't show modal if not properly configured
    if (!this.modal || !this.freeGiftEnabled || !this.freeGiftProductId) {
      // If modal can't be shown, just add to cart without gift decision
      this.addToCart(donationAmount, null);
      return;
    }

    // Close cart drawer if it's open to avoid overlap
    const cartDrawer = document.querySelector('#component-cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('open') && typeof cartDrawer.closeDrawer === 'function') {
      cartDrawer.closeDrawer();
    }

    // Close quickview drawer if it's open to avoid overlap
    const quickshopDrawer = document.querySelector('#quickshop-drawer');
    if (quickshopDrawer && quickshopDrawer.classList.contains('open') && typeof quickshopDrawer.closeDrawer === 'function') {
      quickshopDrawer.closeDrawer();
    }

    // Move modal to body if it's inside a constrained container (like cart drawer)
    // This is necessary because containers with overflow: hidden will clip the modal
    if (this.modal.parentElement !== document.body) {
      // Store original parent if not already stored
      if (!this.modalOriginalParent) {
        this.modalOriginalParent = this.modal.parentElement;
      }
      // Move to body to escape container constraints
      document.body.appendChild(this.modal);
    }

    // Calculate adjusted amount
    const adjustedAmount = this.calculateAdjustedAmount(donationAmount);
    
    // Update the adjusted amount display in modal
    const adjustedAmountElement = this.modal.querySelector('[data-adjusted-amount-value]');
    if (adjustedAmountElement) {
      adjustedAmountElement.textContent = this.formatCurrency(adjustedAmount);
    }

    // Show the modal first
    this.modal.show();

    // Initialize variant selector after modal is shown (DOM is ready)
    // Select first available variant by default if nothing is selected
    setTimeout(() => {
      const variantSelector = this.modal.querySelector('variant-selects');
      if (variantSelector) {
        const fieldsets = variantSelector.querySelectorAll('fieldset[data-optionindex]');
        
        fieldsets.forEach(fieldset => {
          const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
          
          // If no radio is checked, select the first available one
          if (!checkedRadio) {
            const firstAvailableRadio = fieldset.querySelector('input[type="radio"]:not([disabled])');
            if (firstAvailableRadio) {
              firstAvailableRadio.checked = true;
              
              // Update the selected option label
              const selectedLabel = fieldset.querySelector('.selected-option');
              if (selectedLabel) {
                selectedLabel.textContent = firstAvailableRadio.value;
              }
            }
          }
        });
      }
    }, 100);
  }

  /**
   * Calculate adjusted donation amount (donation - free gift price)
   */
  calculateAdjustedAmount(donationAmount) {
    // Convert gift price from cents to dollars if needed
    const giftPriceInDollars = this.freeGiftPrice / 100;
    return Math.max(0, donationAmount - giftPriceInDollars);
  }

  /**
   * Format currency value
   */
  formatCurrency(amount) {
    return `${this.currencySymbol}${amount.toFixed(2)}`;
  }

  /**
   * Get selected variant ID from variant selector
   */
  getSelectedFreeGiftVariantId() {
    const variantSelector = this.modal?.querySelector('variant-selects');
    const variantsJSON = this.modal?.querySelector('[data-variantJSON]');
    
    // If no variants JSON found, try to fetch from product API as fallback
    if (!variantsJSON || !variantsJSON.textContent) {
      console.warn('Variants JSON not found in modal, attempting fallback');
      // Fallback: return null and let handleAcceptGift handle it with product fetch
      return null;
    }
    
    let variants;
    try {
      variants = JSON.parse(variantsJSON.textContent);
    } catch (e) {
      console.error('Error parsing variants:', e);
      return null;
    }
    
    if (!variants || variants.length === 0) {
      console.warn('No variants found in JSON');
      return null;
    }
    
    if (!variantSelector) {
      // If no variant selector (product has only default variant), get first available variant
      const availableVariant = variants.find(v => v.available) || variants[0];
      if (availableVariant && availableVariant.id) {
        return availableVariant.id;
      }
      console.warn('No available variant found for default variant product');
      return null;
    }
    
    // Get selected options from radio buttons
    const selectedOptions = [];
    const fieldsets = variantSelector.querySelectorAll('fieldset[data-optionindex]');
    
    fieldsets.forEach(fieldset => {
      const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
      if (checkedRadio) {
        selectedOptions.push(checkedRadio.value);
      }
    });
    
    if (selectedOptions.length === 0) {
      // No options selected, return first available variant
      const availableVariant = variants.find(v => v.available) || variants[0];
      if (availableVariant && availableVariant.id) {
        return availableVariant.id;
      }
      console.warn('No variant selected and no default variant found');
      return null;
    }
    
    // Find matching variant based on selected options
    const matchedVariant = variants.find(variant => {
      return variant.options && variant.options.every((option, i) => selectedOptions[i] === option);
    });
    
    if (matchedVariant && matchedVariant.id) {
      // Store current variant on the selector for reference
      variantSelector.currentVariant = matchedVariant;
      return matchedVariant.id;
    }
    
    // Fallback: get first available variant
    const availableVariant = variants.find(v => v.available) || variants[0];
    if (availableVariant && availableVariant.id) {
      return availableVariant.id;
    }
    
    console.warn('No matching variant found');
    return null;
  }

  /**
   * Handle accepting the free gift
   */
  async handleAcceptGift(event) {
    this.giftAccepted = true;
    
    // Get selected variant ID from selector
    let selectedVariantId = this.getSelectedFreeGiftVariantId();
    
    // If no variant found, try to fetch default variant from product API
    if (!selectedVariantId && this.freeGiftProductId) {
      try {
        // Try to get product handle from global settings or modal data
        let productHandle = null;
        if (window.donationSettings && window.donationSettings.freeGiftProductHandle) {
          productHandle = window.donationSettings.freeGiftProductHandle;
        } else {
          // Fallback: try to extract from modal or use product ID
          const productLink = this.modal?.querySelector('a[href*="/products/"]');
          if (productLink) {
            const match = productLink.href.match(/\/products\/([^\/]+)/);
            if (match) {
              productHandle = match[1];
            }
          }
        }
        
        if (productHandle) {
          const productResponse = await fetch(`/products/${productHandle}.js`);
          if (productResponse.ok) {
            const product = await productResponse.json();
            // Get first available variant or first variant
            const defaultVariant = product.variants?.find(v => v.available) || product.variants?.[0];
            if (defaultVariant && defaultVariant.id) {
              selectedVariantId = defaultVariant.id;
              console.log('Fetched default variant from product API:', selectedVariantId);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching product for default variant:', error);
      }
    }
    
    if (!selectedVariantId) {
      console.error('No variant selected for free gift and could not fetch default variant');
      Utility.handleError(new Error('Unable to add free gift. Please try again or contact support.'));
      return;
    }
    
    // Store selected variant ID
    this.selectedFreeGiftVariantId = selectedVariantId;
    
    // Calculate adjusted donation amount
    const adjustedAmount = this.calculateAdjustedAmount(this.pendingDonationAmount);
    
    // Check if we're updating existing donation or adding new
    if (this.existingDonationToUpdate) {
      // Update existing donation with gift acceptance
      this.continueUpdateWithGiftDecision(adjustedAmount, true, event.currentTarget);
    } else {
      // Add to cart with items array (donation + free gift)
      this.addToCart(adjustedAmount, true, event.currentTarget);
    }
  }

  /**
   * Handle declining the free gift
   */
  handleDeclineGift(event) {
    this.giftAccepted = false;
    
    // Check if we're updating existing donation or adding new
    if (this.existingDonationToUpdate) {
      // Update existing donation without gift
      this.continueUpdateWithGiftDecision(this.pendingDonationAmount, false, event.currentTarget);
    } else {
      // Add to cart with original amount (no free gift)
      this.addToCart(this.pendingDonationAmount, false, event.currentTarget);
    }
  }

  /**
   * Continue updating existing donation after gift decision
   */
  async continueUpdateWithGiftDecision(donationAmount, includeGift, clickedButton) {
    const originalDonationAmount = this.pendingDonationAmount;
    
    const donationProperties = {
      '_custom_price': originalDonationAmount.toFixed(2),
      '_donation_contribution_price': donationAmount.toFixed(2),
      '_item_type': 'Contribution',
      '_free_gift_opt_in': includeGift ? 'true' : 'false'
    };
    
    if (includeGift) {
      const variantId = this.selectedFreeGiftVariantId || this.getSelectedFreeGiftVariantId();
      if (variantId) {
        donationProperties['_original_donation_price'] = originalDonationAmount.toFixed(2);
        donationProperties['_free_gift_variant_id'] = variantId;
        donationProperties['_free_gift_price'] = (this.freeGiftPrice / 100).toFixed(2);
      }
    }
    
    await this.updateExistingDonation(
      this.existingDonationToUpdate, 
      donationProperties, 
      donationAmount, 
      includeGift, 
      clickedButton
    );
    
    // Clear the reference
    this.existingDonationToUpdate = null;
  }

  /**
   * Add or update donation in cart using Shopify Ajax API
   * Only adds/updates ONE item with all gift information in properties
   * Backend will handle adding the free gift based on these properties
   */
  async addToCart(donationAmount, includeGift, clickedButton = null) {
    // Get donation product variant ID from form
    const donationVariantId = this.form.querySelector('input[name="id"]').value;
    
    // Build donation properties
    const originalDonationAmount = this.pendingDonationAmount || donationAmount;
    
    const donationProperties = {
      '_custom_price': originalDonationAmount.toFixed(2),  // Hidden - original donation amount
      '_donation_contribution_price': donationAmount.toFixed(2),  // Hidden - adjusted amount (if gift accepted)
      '_item_type': 'Contribution'
    };
    
    // Only add gift opt-in property if user explicitly made a choice and gift is configured
    if (includeGift !== null && this.freeGiftEnabled && this.freeGiftProductId) {
      donationProperties['_free_gift_opt_in'] = includeGift ? 'true' : 'false';
      
      // Add additional properties if gift was accepted
      if (includeGift) {
        const variantId = this.selectedFreeGiftVariantId || this.getSelectedFreeGiftVariantId();
        if (variantId) {
          donationProperties['_original_donation_price'] = originalDonationAmount.toFixed(2);
          donationProperties['_free_gift_variant_id'] = variantId;
          donationProperties['_free_gift_price'] = (this.freeGiftPrice / 100).toFixed(2);
        }
      }
    }
    
    // Check if donation already exists in cart
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const existingDonation = this.findExistingDonation(cart, donationVariantId);
      
      if (existingDonation) {
        // Check if we need to show gift modal for combined amount
        const existingCustomPrice = parseFloat(existingDonation.properties._custom_price) || 0;
        const newTotalAmount = existingCustomPrice + originalDonationAmount;
        const hasExistingGiftDecision = existingDonation.properties._free_gift_opt_in !== undefined;
        
        // If combined total exceeds threshold and no gift decision made yet, show modal
        if (this.freeGiftEnabled && 
            newTotalAmount >= this.freeGiftThreshold && 
            !hasExistingGiftDecision && 
            includeGift === null) {
          // Store the existing donation reference for later
          this.existingDonationToUpdate = existingDonation;
          this.pendingDonationAmount = originalDonationAmount;
          this.showFreeGiftModal(originalDonationAmount);
          
          // Remove loading states since we're showing modal
          if (clickedButton) {
            clickedButton.disabled = false;
            clickedButton.classList.remove('loading');
          } else {
            if (this.submitButton) {
              this.submitButton.classList.remove('loading');
            }
            Utility.setLoadingState(false, this.submitButton);
          }
          return;
        }
        
        // Add new donation amount on top of existing donation
        await this.updateExistingDonation(existingDonation, donationProperties, donationAmount, includeGift, clickedButton);
        return;
      }
    } catch (error) {
      console.error('Error checking cart:', error);
      // Continue to add new item if check fails
    }
    
    // Build items array with single item (for new donation)
    const items = [
      {
        id: donationVariantId,
        quantity: 1,
        properties: donationProperties
      }
    ];
    
    // Build cart attributes to track donation at cart level
    const cartAttributes = {
      '_original_donation_price': originalDonationAmount.toFixed(2)
    };
    
    // Add loading state only to the specific button clicked
    if (clickedButton) {
      // Modal button was clicked - only show loading on that button
      clickedButton.disabled = true;
      clickedButton.classList.add('loading');
    } else {
      // Direct form submit - show loading on submit button
      if (this.submitButton) {
        this.submitButton.classList.add('loading');
      }
      // Use theme's default loading state
      Utility.setLoadingState(true, this.submitButton);
    }
    
    // Get cart element for sections update
    const cartElement = document.querySelector('cart-form');
    const cartSectionId = cartElement?.dataset?.sectionId || 'template-cart';
    
    // Make API call to add items to cart
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ 
        items: items,
        attributes: cartAttributes,
        sections: cartSectionId
      })
    })
    .then(response => response.json())
    .then(data => {
      // Check for errors in response
      if (data.status === 422 || data.message || data.description) {
        throw new Error(data.message || data.description || 'Unable to add to cart');
      }
      
      // Publish event for theme to handle
      if (typeof eventBus !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        eventBus.publish(PUB_SUB_EVENTS.productATC, {
          data: {
            item: data,
            sections: data.sections
          }
        });
      }
      
      // Update cart using theme's default method
      if (cartElement) {
        if (data.sections && typeof cartElement._updateCart === 'function') {
          // Update cart with sections
          cartElement._updateCart(data.sections, this.submitButton);
        } else if (typeof cartElement.getCartData === 'function') {
          // Fallback: fetch fresh cart data
          cartElement.getCartData(this.submitButton);
        } else {
          // Last resort: manual update
          this.updateCartManually(data);
        }
        
        this.openCartDrawer();
      } else {
        // No cart element found: manual update and try to open drawer
        this.updateCartManually(data);
        this.openCartDrawer();
      }
      
      // Close free gift modal on success
      if (this.modal && this.modal.modal?.classList.contains('open')) {
        this.modal.hide();
      }
      
      // Close donation modal if it's open
      const donationModal = this.closest('modal-dialog.donation-modal');
      if (donationModal && typeof donationModal.hide === 'function') {
        donationModal.hide();
      }
      
      // Reset state for next donation
      this.resetDonationState();
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      Utility.handleError(error);
    })
    .finally(() => {
      // Remove loading state from the button that was clicked
      if (clickedButton) {
        // Modal button - only clean up that button
        clickedButton.disabled = false;
        clickedButton.classList.remove('loading');
      } else {
        // Submit button - clean up submit button
        if (this.submitButton) {
          this.submitButton.classList.remove('loading');
        }
        // Use theme's default loading state removal
        Utility.setLoadingState(false, this.submitButton);
      }
    });
  }

  /**
   * Fallback manual cart update if cart element not available
   */
  updateCartManually(data) {
    const cartElement = document.querySelector('cart-form');
    const cartSectionId = cartElement?.dataset?.sectionId || 'template-cart';
    
    if (data.sections && data.sections[cartSectionId]) {
      let cartHTML = new DOMParser().parseFromString(data.sections[cartSectionId], 'text/html');
      const cartJSONEle = cartHTML.querySelector('[data-cartScriptJSON]');
      
      if (cartJSONEle) {
        window.globalSpace = window.globalSpace || {};
        window.globalSpace.cart = JSON.parse(cartJSONEle.textContent);
      }
    }
    
    // Update cart count
    if (typeof updateCartCountInHeader !== 'undefined') {
      updateCartCountInHeader();
    }
  }

  /**
   * Open cart drawer directly
   */
  openCartDrawer() {
    // Close donation modal if it's open
    const donationModal = this.closest('modal-dialog.donation-modal');
    if (donationModal && typeof donationModal.hide === 'function') {
      donationModal.hide();
    }
    
    const cartDrawer = document.querySelector('#component-cart-drawer');
    if (cartDrawer && typeof cartDrawer.openDrawer === 'function') {
      setTimeout(() => {
        cartDrawer.openDrawer(this.submitButton);
      }, 100);
    }
  }

  /**
   * Reset donation state for next donation
   */
  resetDonationState() {
    // Reset gift acceptance state
    this.giftAccepted = null;
    this.pendingDonationAmount = null;
    
    // Clear form inputs
    this.amountRadios.forEach(radio => radio.checked = false);
    if (this.customAmountInput) {
      this.customAmountInput.value = '';
    }
    
    // Clear custom price input
    const customPriceInput = this.querySelector('input[name="properties[_custom_price]"]');
    if (customPriceInput) {
      customPriceInput.value = '';
    }
    
    // Disable submit button
    if (this.submitButton) {
      this.submitButton.disabled = true;
    }
  }

  /**
   * Find existing donation item in cart
   */
  findExistingDonation(cart, donationVariantId) {
    if (!cart || !cart.items) return null;
    
    // Check if any cart item matches the donation variant
    return cart.items.find(item => {
      // Check if item is the donation product (by variant ID)
      if (String(item.variant_id) === String(donationVariantId)) {
        return true;
      }
      
      // Also check if item has donation properties (for parent donation product)
      if (item.properties && item.properties._item_type === 'Contribution') {
        return true;
      }
      
      return false;
    });
  }

  /**
   * Update existing donation item in cart by adding new amount on top
   */
  async updateExistingDonation(existingItem, newProperties, newDonationAmount, includeGift, clickedButton = null) {
    // Add loading state to the specific button clicked
    if (clickedButton) {
      clickedButton.disabled = true;
      clickedButton.classList.add('loading');
    } else {
      if (this.submitButton) {
        this.submitButton.classList.add('loading');
      }
      Utility.setLoadingState(true, this.submitButton);
    }
    
    try {
      // Get existing donation amount from properties
      const existingCustomPrice = parseFloat(existingItem.properties._custom_price) || 0;
      const existingContributionPrice = parseFloat(existingItem.properties._donation_contribution_price) || 0;
      
      // Calculate new combined amounts
      const newCustomPrice = existingCustomPrice + parseFloat(newProperties._custom_price);
      const newContributionPrice = existingContributionPrice + newDonationAmount;
      
      // Build updated properties with combined amounts
      const updatedProperties = {
        '_custom_price': newCustomPrice.toFixed(2),
        '_donation_contribution_price': newContributionPrice.toFixed(2),
        '_item_type': 'Contribution'
      };
      
      // Preserve or add gift opt-in status
      if (existingItem.properties._free_gift_opt_in !== undefined) {
        // Keep existing gift decision
        updatedProperties['_free_gift_opt_in'] = existingItem.properties._free_gift_opt_in;
      } else if (newProperties._free_gift_opt_in !== undefined) {
        // Add new gift decision
        updatedProperties['_free_gift_opt_in'] = newProperties._free_gift_opt_in;
      }
      
      // If gift was accepted (either existing or new), update the original donation price
      if (includeGift) {
        const variantId = this.selectedFreeGiftVariantId || this.getSelectedFreeGiftVariantId();
        if (variantId) {
          const existingOriginalPrice = parseFloat(existingItem.properties._original_donation_price) || existingCustomPrice;
          const newOriginalAmount = this.pendingDonationAmount || parseFloat(newProperties._custom_price);
          updatedProperties['_original_donation_price'] = (existingOriginalPrice + newOriginalAmount).toFixed(2);
          updatedProperties['_free_gift_variant_id'] = variantId;
          updatedProperties['_free_gift_price'] = newProperties._free_gift_price;
        }
      } else if (existingItem.properties._free_gift_opt_in === 'true') {
        // Preserve existing gift properties
        if (existingItem.properties._original_donation_price) {
          const existingOriginalPrice = parseFloat(existingItem.properties._original_donation_price);
          const newOriginalAmount = this.pendingDonationAmount || parseFloat(newProperties._custom_price);
          updatedProperties['_original_donation_price'] = (existingOriginalPrice + newOriginalAmount).toFixed(2);
        }
        if (existingItem.properties._free_gift_variant_id) {
          updatedProperties['_free_gift_variant_id'] = existingItem.properties._free_gift_variant_id;
        }
        if (existingItem.properties._free_gift_price) {
          updatedProperties['_free_gift_price'] = existingItem.properties._free_gift_price;
        }
      }
      
      // First, update the line item using cart/change.js
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: existingItem.key,
          quantity: 1,
          properties: updatedProperties
        })
      });
      
      const data = await response.json();
      
      if (data.status === 422 || data.message || data.description) {
        throw new Error(data.message || data.description || 'Unable to update cart');
      }
      
      // Now update cart attributes to reflect the updated total
      const cartAttributes = {
        '_original_donation_price': newCustomPrice.toFixed(2)
      };
      
      // Update cart attributes using /cart/update.js
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          attributes: cartAttributes
        })
      });
      
      // Publish event for theme to handle
      if (typeof eventBus !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        eventBus.publish(PUB_SUB_EVENTS.productATC, {
          data: {
            item: data,
            sections: data.sections
          }
        });
      }
      
      // Update cart using theme's default method
      const cartElement = document.querySelector('cart-form');
      if (cartElement) {
        if (typeof cartElement.getCartData === 'function') {
          cartElement.getCartData(this.submitButton);
        }
        this.openCartDrawer();
      }
      
      // Close free gift modal on success
      if (this.modal && this.modal.modal?.classList.contains('open')) {
        this.modal.hide();
      }
      
      // Close donation modal if it's open
      const donationModal = this.closest('modal-dialog.donation-modal');
      if (donationModal && typeof donationModal.hide === 'function') {
        donationModal.hide();
      }
      
      // Reset state for next donation
      this.resetDonationState();
      
    } catch (error) {
      console.error('Error updating cart:', error);
      Utility.handleError(error);
    } finally {
      // Remove loading state from the button that was clicked
      if (clickedButton) {
        clickedButton.disabled = false;
        clickedButton.classList.remove('loading');
      } else {
        if (this.submitButton) {
          this.submitButton.classList.remove('loading');
        }
        Utility.setLoadingState(false, this.submitButton);
      }
    }
  }
}

// Register the custom element
customElements.define('donation-component', DonationComponent);
